import { Box, Dialog, DialogContent } from '@mui/material'
import { FC, useEffect, useState } from 'react'
import CloseIcon from '../../../../assets/images/close-icon.svg?react'
import { CUSTOMER_CARE_GOOGLE_FORM_URL } from '../EngagementDetailsEnums'

interface ResolveFormDialogProps {
  isOpen: boolean
  onClose: () => void
}

// Google Forms is cross-origin, so we can't read the iframe's content or get a submit callback.
// The iframe's `load` event fires on this element (same-origin to us) every time its nested
// document navigates, even cross-origin - the first load is the form itself, and a second load
// means Google Forms internally navigated to its post-submit confirmation page.
const SUBMITTED_LOAD_COUNT = 2

export const ResolveFormDialog: FC<ResolveFormDialogProps> = ({ isOpen, onClose }) => {
  const [loadCount, setLoadCount] = useState(0)

  useEffect(() => {
    if (isOpen) {
      setLoadCount(0)
    }
  }, [isOpen])

  // We want to close the dialog and attempt to close the tab once the iframe has loaded twice (indicating form submission).
  useEffect(() => {
    if (loadCount >= SUBMITTED_LOAD_COUNT) {
      onClose()
      // Only succeeds if this tab has a window.opener (e.g. opened via a new-tab link from
      // another site without rel="noopener") - otherwise this is a harmless no-op and the
      // agent is simply left on the ERT page, which is a safe fallback either way.
      window.close()
    }
  }, [loadCount, onClose])

  const closeAfterReload = () => {
    setLoadCount(currentLoadCount => currentLoadCount + 1)
  }

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="lg" fullWidth data-testid="resolve-form-dialog">
      <DialogContent sx={{ padding: 0, height: '100vh', overflowY: 'hidden' }}>
        <Box
          onClick={onClose}
          data-testid="resolve-form-close-button"
          sx={{ position: 'absolute', top: 'var(--spacing-2xs)', right: 'var(--spacing-2xs)', cursor: 'pointer', zIndex: 1 }}
        >
          <CloseIcon />
        </Box>
        <iframe
          src={CUSTOMER_CARE_GOOGLE_FORM_URL}
          onLoad={closeAfterReload}
          title="Resolve Engagement Form"
          style={{ width: '100%', height: '100%', border: 'none' }}
          data-testid="resolve-form-iframe"
        />
      </DialogContent>
    </Dialog>
  )
}
